import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useMessageModal } from "../admin/contexts/MessageModalContext";
import { useFormat } from "../context/FormatContext";
import { useCurrencyConverter } from "../context/CurrencyConverterContext";
import DataTable, { type Column } from "../admin/components/DataTable";
import "../styles/marketPriceAnalysis.css";

type RegionOption = {
  id: number;
  region_name: string;
  region_code?: string | null;
  country_code?: string | null;
  dataforseo_location_name?: string | null;
  dataforseo_location_code?: number | null;
  dataforseo_language_code?: string | null;
  currency_code?: string | null;
};

type MarketResult = {
  id?: number;
  title?: string;
  sellerName?: string;
  seller_name?: string;
  domain?: string;
  url?: string;
  priceValue?: number;
  price_value?: number;
  currency?: string;
  currencyCode?: string;
  rating?: number | null;
  reviewsCount?: number | null;
  reviews_count?: number | null;
  rankPosition?: number | null;
  rank_position?: number | null;
  matchScore?: number;
  match_score?: number;
  matchType?: "Exact" | "Similar" | "Weak" | "Unmatched";
  match_type?: "Exact" | "Similar" | "Weak" | "Unmatched";
};

type NormalizedMarketResult = {
  id?: number;
  title: string;
  sellerName: string;
  domain: string;
  url: string;
  priceValue: number | null;
  currency: string;
  currencyCode?: string;
  rating: number | null;
  reviewsCount: number | null;
  rankPosition: number | null;
  matchScore: number;
  matchType: "Exact" | "Similar" | "Weak" | "Unmatched";
};

type MarketAnalysisResponse = {
  runId: number;
  searchKeyword: string;
  locationName?: string;
  currencyCode?: string;
  lowestPrice: number | null;
  averagePrice: number | null;
  highestPrice: number | null;
  resultCount: number;
  accessType: "Free" | "Paid";
  chargedAmount: number;
  wallet?: {
    balanceAmount: number;
    freeSearchUsed: boolean;
  };
  marketTrend?: {
    rangeDays: number;
    points: MarketTrendPoint[];
  };
  aiSuggestion?: MarketAiSuggestion;
  results: MarketResult[];
};

type WalletTransaction = {
  id: number;
  transaction_type: "Credit" | "Debit" | "FreeSearch";
  amount: number;
  reference_type?: string | null;
  reference_id?: string | null;
  note?: string | null;
  created_at: string;

  search_keyword?: string | null;
  access_type?: "Free" | "Paid" | null;
  analysis_status?: string | null;
  result_count?: number | null;
};

type WalletTopup = {
  id: number;
  payment_ref: string;
  plan_id: number;
  amount: number;
  payment_currency?: string | null;
  title?: string | null;
  description?: string | null;
  payment_method?: string | null;
  payment_provider?: "SecurePay" | "Stripe" | string | null;
  status: "Pending" | "Success" | "Failed" | "Cancelled";
  securepay_transaction_id?: string | null;
  stripe_payment_intent_id?: string | null;
  wallet_credited: number;
  created_at: string;
  updated_at: string;
};

type WalletHistoryData = {
  wallet: {
    balanceAmount: number;
    freeSearchUsed: boolean;
  };
  transactions: WalletTransaction[];
  topups: WalletTopup[];
};

type MarketTrendPoint = {
  date_label: string;
  lowest_price: number | null;
  average_price: number | null;
  highest_price: number | null;
  total_runs: number;
};

type MarketAiSuggestion = {
  trendDirection: "up" | "down" | "stable" | "insufficient";
  changePercent: number;
  seller: {
    suggestedPrice: number | null;
    message: string;
  };
  buyer: {
    targetPrice: number | null;
    message: string;
  };
  marketRange?: {
    lowest: number;
    average: number;
    highest: number;
  };
};

const API_BASE_URL =
  import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000";

const getStoredSubscriberUser = () => {
  try {
    const storedSubscriberRaw = localStorage.getItem("subscriber_user");

    if (!storedSubscriberRaw) return null;

    const subscriberUser = JSON.parse(storedSubscriberRaw);

    if (!subscriberUser?.id) return null;

    if (
      subscriberUser?.tokenExpiry &&
      Number(subscriberUser.tokenExpiry) < Date.now()
    ) {
      localStorage.removeItem("subscriber_user");
      return null;
    }

    return subscriberUser;
  } catch (error) {
    console.error("Invalid subscriber_user in localStorage:", error);
    localStorage.removeItem("subscriber_user");
    return null;
  }
};

const MarketPriceTrendChart = ({
  points,
  formatMarketResultCurrency,
}: {
  points: MarketTrendPoint[];
  formatMarketResultCurrency: (value?: number | null) => string;
}) => {
  const validPoints = points.filter(
    (item) => Number(item.average_price || 0) > 0,
  );

  if (!validPoints.length) {
    return (
      <div className="market-trend-empty">
        No historical price fluctuation data found yet.
      </div>
    );
  }

  const values = validPoints.map((item) => Number(item.average_price || 0));
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue || 1;

  // Dynamic width prevents 30-day labels from overlapping
  const width = Math.max(760, validPoints.length * 58);
  const height = 290;

  const paddingX = 44;
  const paddingTop = 34;
  const paddingBottom = 56;

  const chartWidth = width - paddingX * 2;
  const chartHeight = height - paddingTop - paddingBottom;

  const getX = (index: number) => {
    if (validPoints.length === 1) return width / 2;

    return paddingX + (index / (validPoints.length - 1)) * chartWidth;
  };

  const getY = (value: number) => {
    return paddingTop + ((maxValue - value) / range) * chartHeight;
  };

  const getDateParts = (dateValue: string) => {
    const date = new Date(dateValue);

    const day = date.toLocaleDateString("en-MY", {
      day: "2-digit",
    });

    const month = date.toLocaleDateString("en-MY", {
      month: "short",
    });

    return {
      day,
      month,
    };
  };

  const linePoints = validPoints
    .map((item, index) => {
      const x = getX(index);
      const y = getY(Number(item.average_price || 0));

      return `${x},${y}`;
    })
    .join(" ");

  return (
    <div className="market-trend-chart-wrap">
      <div className="market-trend-chart-meta">
        <span>Lowest: {formatMarketResultCurrency(minValue)}</span>
        <span>Highest: {formatMarketResultCurrency(maxValue)}</span>
      </div>

      <svg viewBox={`0 0 ${width} ${height}`} className="market-trend-chart">
        {validPoints.map((item, index) => {
          const value = Number(item.average_price || 0);
          const x = getX(index);
          const y = getY(value);
          const barHeight = height - paddingBottom - y;
          const dateParts = getDateParts(item.date_label);

          return (
            <g key={`${item.date_label}-${index}`}>
              <rect
                x={x - 10}
                y={y}
                width="20"
                height={barHeight}
                rx="6"
                className="market-trend-bar"
              />

              <circle cx={x} cy={y} r="5" className="market-trend-dot" />

              <text
                x={x}
                y={height - 34}
                textAnchor="middle"
                className="market-trend-date-label"
              >
                <tspan x={x}>{dateParts.day}</tspan>
                <tspan x={x} dy="13">
                  {dateParts.month}
                </tspan>
              </text>

              <title>{`${item.date_label}: ${formatMarketResultCurrency(value)}`}</title>
            </g>
          );
        })}

        <polyline points={linePoints} className="market-trend-line" />
      </svg>
    </div>
  );
};

const MarketPriceAnalysis: React.FC = () => {
  const navigate = useNavigate();

  const { showModal } = useMessageModal();
  const { formatDateToDMY } = useFormat();
  const { formatLocalCurrencyOnly } = useCurrencyConverter();

  const [storedUser, setStoredUser] = useState<any>(() =>
    getStoredSubscriberUser(),
  );

  const exportRef = useRef<HTMLDivElement | null>(null);

  const LAST_SEARCH_STORAGE_KEY = "lyrazone_market_analysis_last_result";

  const [regions, setRegions] = useState<RegionOption[]>([]);
  const [countrySearch, setCountrySearch] = useState(
    storedUser?.country || "Malaysia",
  );
  const [selectedCountryName, setSelectedCountryName] = useState(
    storedUser?.country || "Malaysia",
  );
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  const countryDropdownRef = useRef<HTMLDivElement | null>(null);
  const pdfReportRef = useRef<HTMLDivElement | null>(null);

  const [searchKeyword, setSearchKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<MarketAnalysisResponse | null>(null);
  const [error, setError] = useState("");
  const [accessMessage, setAccessMessage] = useState("");
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [walletActiveTab, setWalletActiveTab] = useState<
    "transactions" | "topups"
  >("transactions");
  const [walletHistory, setWalletHistory] = useState<WalletHistoryData | null>(
    null,
  );
  const [walletHistoryLoading, setWalletHistoryLoading] = useState(false);
  const [walletHistoryError, setWalletHistoryError] = useState("");
  const [trendRange, setTrendRange] = useState<30 | 90>(30);
  const [marketTrendPoints, setMarketTrendPoints] = useState<
    MarketTrendPoint[]
  >([]);
  const [aiSuggestion, setAiSuggestion] = useState<MarketAiSuggestion | null>(
    null,
  );
  const [trendLoading, setTrendLoading] = useState(false);

  useEffect(() => {
    try {
      const savedRaw = localStorage.getItem(LAST_SEARCH_STORAGE_KEY);

      if (!savedRaw) return;

      const saved = JSON.parse(savedRaw);

      if (saved?.analysis) {
        setAnalysis(saved.analysis);
        setMarketTrendPoints(saved.marketTrendPoints || []);
        setAiSuggestion(saved.aiSuggestion || null);

        if (saved.analysis?.wallet) {
          setWalletBalance(Number(saved.analysis.wallet.balanceAmount || 0));
        }

        if (saved.analysis?.searchKeyword) {
          setSearchKeyword(saved.analysis.searchKeyword);
        }

        if (saved.analysis?.locationName) {
          setCountrySearch(saved.analysis.locationName);
          setSelectedCountryName(saved.analysis.locationName);
        }
      }
    } catch (error) {
      console.error("Restore last Market Analysis result error:", error);
    }
  }, []);

  useEffect(() => {
    const refreshStoredUser = () => {
      setStoredUser(getStoredSubscriberUser());
    };

    refreshStoredUser();

    window.addEventListener("focus", refreshStoredUser);
    window.addEventListener("storage", refreshStoredUser);

    return () => {
      window.removeEventListener("focus", refreshStoredUser);
      window.removeEventListener("storage", refreshStoredUser);
    };
  }, []);

  useEffect(() => {
    fetchSubscriberWallet();

    const refreshWalletOnFocus = () => {
      fetchSubscriberWallet();
    };

    window.addEventListener("focus", refreshWalletOnFocus);

    return () => {
      window.removeEventListener("focus", refreshWalletOnFocus);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchRegions = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/regions`, {
          withCredentials: true,
        });

        if (!isMounted || !response.data?.success) return;

        const regionList: RegionOption[] = response.data.data || [];
        setRegions(regionList);

        const defaultCountry = storedUser?.country || "Malaysia";

        const matchedRegion = regionList.find(
          (region) =>
            String(region.region_name || "").toLowerCase() ===
              String(defaultCountry || "").toLowerCase() ||
            String(region.country_code || "").toLowerCase() ===
              String(defaultCountry || "").toLowerCase(),
        );

        if (matchedRegion) {
          setCountrySearch(matchedRegion.region_name);
          setSelectedCountryName(matchedRegion.region_name);
          setSelectedCountryCode(matchedRegion.country_code || "");
        } else {
          setCountrySearch(defaultCountry);
          setSelectedCountryName(defaultCountry);
          setSelectedCountryCode("");
        }
      } catch (error) {
        console.error("Fetch regions error:", error);
      }
    };

    fetchRegions();

    return () => {
      isMounted = false;
    };
  }, [storedUser?.country]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, []);

  const handleLoadLastSearch = async () => {
    try {
      setLoading(true);
      setError("");
      setAccessMessage("");

      const response = await axios.get(
        `${API_BASE_URL}/market-analysis/last-search`,
        {
          withCredentials: true,
        },
      );

      if (!response.data?.success) {
        setError(response.data?.message || "No last search found.");
        return;
      }

      const lastSearchData = response.data.data;

      console.log(lastSearchData, "Last Search");

      setAnalysis(lastSearchData);
      setMarketTrendPoints(lastSearchData?.marketTrend?.points || []);
      setAiSuggestion(lastSearchData?.aiSuggestion || null);

      if (lastSearchData?.wallet) {
        setWalletBalance(Number(lastSearchData.wallet.balanceAmount || 0));
      }

      if (lastSearchData?.searchKeyword) {
        setSearchKeyword(lastSearchData.searchKeyword);
      }

      if (lastSearchData?.locationName) {
        setCountrySearch(lastSearchData.locationName);
        setSelectedCountryName(lastSearchData.locationName);
      }

      localStorage.setItem(
        LAST_SEARCH_STORAGE_KEY,
        JSON.stringify({
          analysis: lastSearchData,
          marketTrendPoints: lastSearchData?.marketTrend?.points || [],
          aiSuggestion: lastSearchData?.aiSuggestion || null,
          savedAt: new Date().toISOString(),
        }),
      );
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to load last search.",
      );
    } finally {
      setLoading(false);
    }
  };

  // const handleExportSearchPdf = async () => {
  //   try {
  //     if (!pdfReportRef.current || !analysis) {
  //       setError("No search result available to export.");
  //       return;
  //     }

  //     const html2canvas = (await import("html2canvas")).default;
  //     const jsPDFModule = await import("jspdf");
  //     const jsPDF = jsPDFModule.default;

  //     const canvas = await html2canvas(pdfReportRef.current, {
  //       scale: 2,
  //       useCORS: true,
  //       backgroundColor: "#f8f6f0",
  //       windowWidth: 1200,
  //       ignoreElements: (element) => {
  //         return element.classList?.contains("no-print");
  //       },
  //     });

  //     const imgData = canvas.toDataURL("image/png");

  //     const pdf = new jsPDF("p", "mm", "a4");

  //     const pageWidth = pdf.internal.pageSize.getWidth();
  //     const pageHeight = pdf.internal.pageSize.getHeight();

  //     const margin = 10;
  //     const imgWidth = pageWidth - margin * 2;
  //     const imgHeight = (canvas.height * imgWidth) / canvas.width;

  //     let heightLeft = imgHeight;
  //     let position = margin;

  //     pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
  //     heightLeft -= pageHeight - margin * 2;

  //     while (heightLeft > 0) {
  //       position = heightLeft - imgHeight + margin;
  //       pdf.addPage();
  //       pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
  //       heightLeft -= pageHeight - margin * 2;
  //     }

  //     const fileName = `Market-Analysis-${analysis.searchKeyword || "Search"}-${
  //       analysis.locationName || selectedCountryName || "Market"
  //     }.pdf`
  //       .replace(/\s+/g, "-")
  //       .replace(/[^\w.-]/g, "");

  //     pdf.save(fileName);
  //   } catch (error) {
  //     console.error("Export PDF error:", error);
  //     setError("Failed to export search result PDF.");
  //   }
  // };

  const REPORT_URL = "https://lyrazone.com.my/market-price-analysis";

  const getReportDateTime = () => {
    return new Intl.DateTimeFormat("en-MY", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(new Date());
  };

  // const handleExportSearchPdf = async () => {
  //   try {
  //     if (!pdfReportRef.current || !analysis) {
  //       setError("No search result available to export.");
  //       return;
  //     }

  //     const html2canvas = (await import("html2canvas")).default;
  //     const jsPDFModule = await import("jspdf");
  //     const jsPDF = jsPDFModule.default;

  //     const generatedAt = getReportDateTime();

  //     const canvas = await html2canvas(pdfReportRef.current, {
  //       scale: 2,
  //       useCORS: true,
  //       backgroundColor: "#f8f6f0",
  //       windowWidth: 1200,
  //       ignoreElements: (element) => {
  //         return element.classList?.contains("no-print");
  //       },
  //     });

  //     const imgData = canvas.toDataURL("image/png");

  //     const pdf = new jsPDF("p", "mm", "a4");

  //     const pageWidth = pdf.internal.pageSize.getWidth();
  //     const pageHeight = pdf.internal.pageSize.getHeight();

  //     const margin = 10;
  //     const headerHeight = 14;
  //     const footerHeight = 12;

  //     const contentX = margin;
  //     const contentY = margin + headerHeight;
  //     const contentWidth = pageWidth - margin * 2;
  //     const contentHeight =
  //       pageHeight - margin * 2 - headerHeight - footerHeight;

  //     const imgWidth = contentWidth;
  //     const imgHeight = (canvas.height * imgWidth) / canvas.width;

  //     let heightLeft = imgHeight;
  //     let position = contentY;

  //     pdf.addImage(imgData, "PNG", contentX, position, imgWidth, imgHeight);
  //     heightLeft -= contentHeight;

  //     while (heightLeft > 0) {
  //       position = contentY - (imgHeight - heightLeft);
  //       pdf.addPage();
  //       pdf.addImage(imgData, "PNG", contentX, position, imgWidth, imgHeight);
  //       heightLeft -= contentHeight;
  //     }

  //     const totalPages = pdf.getNumberOfPages();

  //     for (let page = 1; page <= totalPages; page += 1) {
  //       pdf.setPage(page);

  //       // Header
  //       pdf.setFont("helvetica", "bold");
  //       pdf.setFontSize(9);
  //       pdf.setTextColor(25, 25, 25);
  //       pdf.text(
  //         "LyraZone Solutions | Market Price Analysis Report",
  //         margin,
  //         9,
  //       );

  //       pdf.setFont("helvetica", "normal");
  //       pdf.setFontSize(8);
  //       pdf.setTextColor(90, 90, 90);
  //       pdf.text(`Generated: ${generatedAt}`, margin, 13);

  //       pdf.text(REPORT_URL, pageWidth - margin, 13, {
  //         align: "right",
  //       });

  //       // Header line
  //       pdf.setDrawColor(209, 157, 40);
  //       pdf.setLineWidth(0.25);
  //       pdf.line(margin, 16, pageWidth - margin, 16);

  //       // Footer line
  //       pdf.setDrawColor(229, 220, 199);
  //       pdf.setLineWidth(0.25);
  //       pdf.line(margin, pageHeight - 13, pageWidth - margin, pageHeight - 13);

  //       // Footer
  //       pdf.setFont("helvetica", "normal");
  //       pdf.setFontSize(8);
  //       pdf.setTextColor(90, 90, 90);

  //       pdf.text(REPORT_URL, margin, pageHeight - 8);

  //       pdf.text(`Generated: ${generatedAt}`, pageWidth / 2, pageHeight - 8, {
  //         align: "center",
  //       });

  //       pdf.text(
  //         `Page ${page} of ${totalPages}`,
  //         pageWidth - margin,
  //         pageHeight - 8,
  //         {
  //           align: "right",
  //         },
  //       );
  //     }

  //     const fileName = `Market-Analysis-${analysis.searchKeyword || "Search"}-${
  //       analysis.locationName || selectedCountryName || "Market"
  //     }.pdf`
  //       .replace(/\s+/g, "-")
  //       .replace(/[^\w.-]/g, "");

  //     pdf.save(fileName);
  //   } catch (error) {
  //     console.error("Export PDF error:", error);
  //     setError("Failed to export search result PDF.");
  //   }
  // };

  const handleExportSearchPdf = async () => {
    try {
      if (!pdfReportRef.current || !analysis) {
        setError("No search result available to export.");
        return;
      }

      const html2canvas = (await import("html2canvas")).default;
      const jsPDFModule = await import("jspdf");
      const jsPDF = jsPDFModule.default;

      const generatedAt = getReportDateTime();

      const canvas = await html2canvas(pdfReportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#f8f6f0",
        windowWidth: 1200,
        ignoreElements: (element) => {
          return element.classList?.contains("no-print");
        },
      });

      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const margin = 8;
      const headerHeight = 10;
      const footerHeight = 9;

      const contentX = margin;
      const contentY = margin + headerHeight;
      const contentWidth = pageWidth - margin * 2;
      const contentHeight =
        pageHeight - margin * 2 - headerHeight - footerHeight;

      const pxPerMm = canvas.width / contentWidth;
      const sliceHeightPx = Math.floor(contentHeight * pxPerMm);

      let sourceY = 0;
      let pageNo = 1;

      while (sourceY < canvas.height) {
        if (pageNo > 1) {
          pdf.addPage();
        }

        const currentSliceHeight = Math.min(
          sliceHeightPx,
          canvas.height - sourceY,
        );

        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = currentSliceHeight;

        const ctx = pageCanvas.getContext("2d");

        if (ctx) {
          ctx.fillStyle = "#f8f6f0";
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

          ctx.drawImage(
            canvas,
            0,
            sourceY,
            canvas.width,
            currentSliceHeight,
            0,
            0,
            canvas.width,
            currentSliceHeight,
          );
        }

        const pageImgData = pageCanvas.toDataURL("image/png");
        const pageImgHeight = currentSliceHeight / pxPerMm;

        pdf.addImage(
          pageImgData,
          "PNG",
          contentX,
          contentY,
          contentWidth,
          pageImgHeight,
        );

        sourceY += currentSliceHeight;
        pageNo += 1;
      }

      const totalPages = pdf.getNumberOfPages();

      for (let page = 1; page <= totalPages; page += 1) {
        pdf.setPage(page);

        // Header background
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, contentY - 2, "F");

        // Header
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(8.5);
        pdf.setTextColor(25, 25, 25);
        pdf.text(
          "LyraZone Solutions | Market Price Analysis Report",
          margin,
          7,
        );

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7.5);
        pdf.setTextColor(90, 90, 90);
        pdf.text(`Generated: ${generatedAt}`, margin, 11);

        pdf.text(REPORT_URL, pageWidth - margin, 11, {
          align: "right",
        });

        pdf.setDrawColor(209, 157, 40);
        pdf.setLineWidth(0.2);
        pdf.line(margin, 14, pageWidth - margin, 14);

        // Footer background
        pdf.setFillColor(255, 255, 255);
        pdf.rect(
          0,
          pageHeight - footerHeight - 2,
          pageWidth,
          footerHeight + 2,
          "F",
        );

        pdf.setDrawColor(229, 220, 199);
        pdf.setLineWidth(0.2);
        pdf.line(
          margin,
          pageHeight - footerHeight - 1,
          pageWidth - margin,
          pageHeight - footerHeight - 1,
        );

        // Footer
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7.5);
        pdf.setTextColor(90, 90, 90);

        pdf.text(REPORT_URL, margin, pageHeight - 5);

        pdf.text(`Generated: ${generatedAt}`, pageWidth / 2, pageHeight - 5, {
          align: "center",
        });

        pdf.text(
          `Page ${page} of ${totalPages}`,
          pageWidth - margin,
          pageHeight - 5,
          {
            align: "right",
          },
        );
      }

      const fileName = `Market-Analysis-${analysis.searchKeyword || "Search"}-${
        analysis.locationName || selectedCountryName || "Market"
      }.pdf`
        .replace(/\s+/g, "-")
        .replace(/[^\w.-]/g, "");

      pdf.save(fileName);
    } catch (error) {
      console.error("Export PDF error:", error);
      setError("Failed to export search result PDF.");
    }
  };

  const handleNewSearch = () => {
    setAnalysis(null);
    setSearchKeyword("");
    setMarketTrendPoints([]);
    setAiSuggestion(null);
    setError("");
    setAccessMessage("");
    localStorage.removeItem(LAST_SEARCH_STORAGE_KEY);
  };

  const handleWalletTopUp = () => {
    setWalletModalOpen(false);
    navigate("/market-analysis-pricing");
  };

  const fetchWalletHistory = async () => {
    const latestStoredUser = getStoredSubscriberUser();

    setStoredUser(latestStoredUser);

    if (!latestStoredUser) {
      showModal({
        type: "warning",
        title: "Warning",
        message: "You are not logged in, please login first.",
      });
      return;
    }

    try {
      setWalletHistoryLoading(true);
      setWalletHistoryError("");

      const response = await axios.get(
        `${API_BASE_URL}/market-analysis/wallet/history`,
        {
          withCredentials: true,
        },
      );

      if (response.data?.success) {
        setWalletHistory(response.data.data);

        if (response.data.data?.wallet) {
          setWalletBalance(
            Number(response.data.data.wallet.balanceAmount || 0),
          );
        }
      } else {
        setWalletHistoryError(
          response.data?.message || "Unable to fetch wallet history.",
        );
      }
    } catch (error: any) {
      setWalletHistoryError(
        error?.response?.data?.message ||
          error.message ||
          "Unable to fetch wallet history.",
      );
    } finally {
      setWalletHistoryLoading(false);
    }
  };

  const handleOpenWalletModal = async () => {
    setWalletModalOpen(true);
    setWalletActiveTab("transactions");
    await fetchWalletHistory();
  };

  const fetchSubscriberWallet = async () => {
    const latestStoredUser = getStoredSubscriberUser();

    setStoredUser(latestStoredUser);

    if (!latestStoredUser) {
      setWalletBalance(null);
      return;
    }

    try {
      setWalletLoading(true);

      const response = await axios.get(
        `${API_BASE_URL}/market-analysis/wallet`,
        {
          withCredentials: true,
        },
      );

      if (response.data?.success) {
        setWalletBalance(Number(response.data.data?.balanceAmount || 0));
      }
    } catch (error) {
      console.error("Fetch subscriber wallet error:", error);
      setWalletBalance(null);
    } finally {
      setWalletLoading(false);
    }
  };

  const normalizeMarketResults = (
    results: MarketResult[] = [],
  ): NormalizedMarketResult[] => {
    return results.map((item) => ({
      id: item.id,
      title: item.title || "-",
      sellerName: item.sellerName || item.seller_name || "-",
      domain: item.domain || "-",
      url: item.url || "",
      priceValue:
        item.priceValue !== undefined && item.priceValue !== null
          ? Number(item.priceValue)
          : item.price_value !== undefined && item.price_value !== null
            ? Number(item.price_value)
            : null,
      currency: item.currency || item.currencyCode || "",
      rating:
        item.rating !== undefined && item.rating !== null
          ? Number(item.rating)
          : null,
      reviewsCount:
        item.reviewsCount !== undefined && item.reviewsCount !== null
          ? Number(item.reviewsCount)
          : item.reviews_count !== undefined && item.reviews_count !== null
            ? Number(item.reviews_count)
            : null,
      rankPosition:
        item.rankPosition !== undefined && item.rankPosition !== null
          ? Number(item.rankPosition)
          : item.rank_position !== undefined && item.rank_position !== null
            ? Number(item.rank_position)
            : null,
      matchScore:
        item.matchScore !== undefined && item.matchScore !== null
          ? Number(item.matchScore)
          : item.match_score !== undefined && item.match_score !== null
            ? Number(item.match_score)
            : 0,
      matchType: item.matchType || item.match_type || "Unmatched",
    }));
  };

  const marketResultRows = useMemo(() => {
    return normalizeMarketResults(analysis?.results || []);
  }, [analysis]);

  const normalizeCurrencyCode = (
    currencyCode?: string | null,
    fallback = "",
  ) => {
    return String(currencyCode || fallback)
      .trim()
      .toUpperCase();
  };

  const marketCurrencyCode = useMemo(() => {
    if (analysis?.currencyCode) {
      return normalizeCurrencyCode(analysis.currencyCode, "MYR");
    }

    const rowCurrency = marketResultRows.find((row) => row.currency)?.currency;

    return normalizeCurrencyCode(rowCurrency, "MYR");
  }, [analysis?.currencyCode, marketResultRows]);

  const getMarketRowCurrencyCode = (row?: NormalizedMarketResult) => {
    const rowCurrency = normalizeCurrencyCode(row?.currency || "");

    if (!rowCurrency) {
      return marketCurrencyCode;
    }

    if (rowCurrency === "MYR" && marketCurrencyCode !== "MYR") {
      return marketCurrencyCode;
    }

    return rowCurrency;
  };

  const formatMarketResultCurrency = (
    value?: number | string | null,
    currencyCode?: string | null,
  ) => {
    const amount = Number(value || 0);

    return `${normalizeCurrencyCode(currencyCode)} ${amount.toLocaleString(
      "en-MY",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    )}`;
  };

  const fetchMarketTrend = async ({
    keyword,
    locationName,
    rangeDays,
  }: {
    keyword: string;
    locationName?: string;
    rangeDays: 30 | 90;
  }) => {
    try {
      setTrendLoading(true);

      const response = await axios.get(
        `${API_BASE_URL}/market-analysis/trend`,
        {
          params: {
            searchKeyword: keyword,
            locationName: locationName || "Malaysia",
            rangeDays,
          },
          withCredentials: true,
        },
      );

      if (response.data?.success) {
        setMarketTrendPoints(response.data.data?.points || []);
        setAiSuggestion(response.data.data?.aiSuggestion || null);
      }
    } catch (error) {
      console.error("Fetch market trend error:", error);
    } finally {
      setTrendLoading(false);
    }
  };

  const getTopupPaymentMethodLabel = (item: WalletTopup) => {
    if (item.payment_provider === "Stripe") return "Card Payment";
    if (item.payment_provider === "SecurePay") return "Online Banking";

    if (item.payment_method === "Stripe") return "Card Payment";
    if (item.payment_method === "Online Banking") return "Online Banking";

    return item.payment_method || "-";
  };

  const marketResultColumns: Column[] = useMemo(
    () => [
      {
        key: "rowNo",
        label: "#",
        filterable: false,
        render: (_row, rowIndex) => rowIndex + 1,
      },
      {
        key: "title",
        label: "Market Product",
      },
      {
        key: "sellerName",
        label: "Seller",
      },
      {
        key: "priceValue",
        label: "Price",
        render: (row: NormalizedMarketResult) =>
          formatMarketResultCurrency(
            row.priceValue,
            getMarketRowCurrencyCode(row),
          ),
      },
      {
        key: "matchType",
        label: "Match",
        render: (row: NormalizedMarketResult) => (
          <span
            className={`market-analysis-badge ${String(
              row.matchType || "Unmatched",
            ).toLowerCase()}`}
          >
            {row.matchType || "Unmatched"}
          </span>
        ),
      },
      {
        key: "matchScore",
        label: "Score",
        render: (row: NormalizedMarketResult) => `${row.matchScore || 0}%`,
      },
      {
        key: "rating",
        label: "Rating",
        render: (row: NormalizedMarketResult) =>
          row.rating ? `${row.rating} (${row.reviewsCount || 0})` : "-",
      },
      {
        key: "domain",
        label: "Source",
        render: (row: NormalizedMarketResult) =>
          row.url ? (
            <a
              href={row.url}
              target="_blank"
              rel="noreferrer"
              className="market-analysis-source-link"
              onClick={(event) => event.stopPropagation()}
            >
              View
            </a>
          ) : (
            row.domain || "-"
          ),
      },
    ],
    [marketCurrencyCode],
  );

  const filteredRegions = useMemo(() => {
    const search = countrySearch.toLowerCase().trim();

    if (!search) return regions;

    return regions.filter((region) => {
      return (
        region.region_name?.toLowerCase().includes(search) ||
        region.country_code?.toLowerCase().includes(search) ||
        region.region_code?.toLowerCase().includes(search)
      );
    });
  }, [regions, countrySearch]);

  const handleAnalyze = async () => {
    if (!storedUser) {
      showModal({
        type: "warning",
        title: "Warning",
        message: "You are not logged in, please login first.",
      });
      return;
    }

    if (!searchKeyword.trim()) {
      setError("Please enter a product keyword.");
      return;
    }

    if (!selectedCountryName) {
      setError("Please select a country from the dropdown.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setAccessMessage("");
      setAnalysis(null);

      const response = await axios.post(
        `${API_BASE_URL}/market-analysis/analyze`,
        {
          searchKeyword: searchKeyword.trim(),
          countryName: selectedCountryName,
          countryCode: selectedCountryCode || undefined,
        },
        {
          withCredentials: true,
        },
      );

      if (response.data?.success) {
        const analysisData = response.data.data;

        setAnalysis(analysisData);

        localStorage.setItem(
          LAST_SEARCH_STORAGE_KEY,
          JSON.stringify({
            analysis: analysisData,
            marketTrendPoints: analysisData?.marketTrend?.points || [],
            aiSuggestion: analysisData?.aiSuggestion || null,
            savedAt: new Date().toISOString(),
          }),
        );

        setMarketTrendPoints(analysisData?.marketTrend?.points || []);
        setAiSuggestion(analysisData?.aiSuggestion || null);
        setTrendRange(30);

        if (analysisData?.wallet) {
          setWalletBalance(Number(analysisData.wallet.balanceAmount || 0));
        }
      } else {
        setError(response.data?.message || "Market analysis failed.");
      }
    } catch (err: any) {
      const responseData = err?.response?.data;

      if (err?.response?.status === 401 || responseData?.requiresAuth) {
        showModal({
          type: "warning",
          title: "Warning",
          message: "You are not logged in, please login first.",
        });
        return;
      }

      if (err?.response?.status === 402 || responseData?.requiresAccess) {
        setAccessMessage(
          responseData?.message ||
            "Please get access for more Market Analysis, click here.",
        );
        return;
      }

      setError(
        responseData?.error ||
          responseData?.message ||
          err.message ||
          "Something went wrong.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTrendRangeChange = async (rangeDays: 30 | 90) => {
    setTrendRange(rangeDays);

    if (!analysis?.searchKeyword) return;

    await fetchMarketTrend({
      keyword: analysis.searchKeyword,
      locationName: analysis.locationName || "Malaysia",
      rangeDays,
    });
  };

  return (
    <>
      <div className="market-analysis-page">
        <section className="market-analysis-hero">
          <div className="market-analysis-hero-inner">
            <div className="market-analysis-hero-content">
              <span className="market-analysis-eyebrow">LyraZone Solution</span>

              <h1>Market Price Analysis</h1>

              <p>
                Search {selectedCountryName || "selected country"} market
                pricing by product keyword and compare current online price
                ranges before making your purchase or pricing decision.
              </p>
            </div>

            {storedUser && (
              <div className="market-analysis-wallet-area">
                <button
                  type="button"
                  className="market-wallet-topup-btn market-analysis-hero-topup-btn"
                  onClick={handleWalletTopUp}
                >
                  Top Up Balance
                </button>

                <button
                  type="button"
                  className="market-analysis-wallet-card"
                  onClick={handleOpenWalletModal}
                >
                  <span>Available Balance</span>

                  <strong>
                    {walletLoading
                      ? "Loading..."
                      : formatLocalCurrencyOnly(walletBalance || 0, {
                          showCode: true,
                        })}
                  </strong>

                  <small>Market Analysis Wallet</small>
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="market-analysis-content">
          <div className="market-analysis-search-card">
            <div className="market-analysis-search-title">
              <h2>Search Market Price</h2>
              <p>
                Enter a product name, model, barcode, or keyword to check
                available {selectedCountryName || "selected country"} market
                prices.
              </p>
            </div>

            <div className="market-analysis-search-grid market-analysis-search-grid-with-country">
              <div className="market-analysis-field">
                <label>Select Market Country</label>

                <div
                  className="market-analysis-country-dropdown-wrapper"
                  ref={countryDropdownRef}
                >
                  <input
                    value={countrySearch}
                    onChange={(e) => {
                      setCountrySearch(e.target.value);
                      setSelectedCountryName("");
                      setSelectedCountryCode("");
                      setShowCountryDropdown(true);
                      setAccessMessage("");
                      setError("");
                    }}
                    onFocus={() => setShowCountryDropdown(true)}
                    placeholder="Select country"
                  />

                  <button
                    type="button"
                    className={`market-analysis-country-arrow ${
                      showCountryDropdown ? "open" : ""
                    }`}
                    onClick={() => setShowCountryDropdown((prev) => !prev)}
                    aria-label="Toggle country dropdown"
                  >
                    ▾
                  </button>

                  {showCountryDropdown && (
                    <div className="market-analysis-country-dropdown">
                      {filteredRegions.length > 0 ? (
                        filteredRegions.map((region) => (
                          <button
                            type="button"
                            key={region.id}
                            className="market-analysis-country-option"
                            onClick={() => {
                              setCountrySearch(region.region_name);
                              setSelectedCountryName(region.region_name);
                              setSelectedCountryCode(region.country_code || "");
                              setShowCountryDropdown(false);
                              setAccessMessage("");
                              setError("");
                            }}
                          >
                            <span>{region.region_name}</span>
                            {region.country_code && (
                              <small>{region.country_code}</small>
                            )}
                          </button>
                        ))
                      ) : (
                        <div className="market-analysis-country-empty">
                          No country found.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="market-analysis-field">
                <label>Search Product Keyword</label>

                <input
                  value={searchKeyword}
                  onChange={(e) => {
                    setSearchKeyword(e.target.value);
                    setAccessMessage("");
                    setError("");
                  }}
                  placeholder={`Example: Brand Model of any product of any industry of ${selectedCountryName || "selected country"}`}
                />

                {accessMessage && (
                  <div className="market-analysis-access-message">
                    Please get access for more Market Analysis,{" "}
                    <button
                      type="button"
                      className="market-analysis-hero-topup-btn"
                      onClick={() => navigate("/market-analysis-pricing")}
                    >
                      Add Funds
                    </button>
                  </div>
                )}
              </div>

              <div className="market-analysis-search-actions">
                <button
                  className="market-analysis-btn"
                  onClick={handleAnalyze}
                  disabled={loading}
                >
                  {loading ? "Analyzing..." : "Analyze Market Price"}
                </button>

                {storedUser && (
                  <button
                    type="button"
                    className="market-analysis-last-search-btn"
                    onClick={handleLoadLastSearch}
                    disabled={loading}
                  >
                    Last Search
                  </button>
                )}
              </div>
            </div>

            {error && <div className="market-analysis-error">{error}</div>}
          </div>

          {analysis && (
            <div className="market-analysis-result-actions no-print">
              <button
                type="button"
                className="market-analysis-export-btn"
                onClick={handleExportSearchPdf}
              >
                Export Search
              </button>

              <button
                type="button"
                className="market-analysis-secondary-action-btn"
                onClick={handleLoadLastSearch}
                disabled={loading}
              >
                Last Search
              </button>

              <button
                type="button"
                className="market-analysis-secondary-action-btn"
                onClick={handleNewSearch}
              >
                New Search
              </button>
            </div>
          )}

          {analysis && (
            <>
              <div ref={exportRef} className="market-analysis-export-area">
                <div className="market-analysis-summary">
                  <div className="market-analysis-card">
                    <span>Lowest Market Price</span>
                    <strong>
                      {formatMarketResultCurrency(
                        analysis.lowestPrice,
                        marketCurrencyCode,
                      )}
                    </strong>
                    <small>Based on market results</small>
                  </div>

                  <div className="market-analysis-card">
                    <span>Average Market Price</span>
                    <strong>
                      {formatMarketResultCurrency(
                        analysis.averagePrice,
                        marketCurrencyCode,
                      )}
                    </strong>
                    <small>
                      {analysis.locationName || selectedCountryName} market
                      average
                    </small>
                  </div>

                  <div className="market-analysis-card">
                    <span>Highest Market Price</span>
                    <strong>
                      {formatMarketResultCurrency(
                        analysis.highestPrice,
                        marketCurrencyCode,
                      )}
                    </strong>
                    <small>Based on market results</small>
                  </div>

                  <div className="market-analysis-card">
                    <span>Results Found</span>
                    <strong>{analysis.resultCount}</strong>
                    <small>
                      {analysis.accessType === "Free"
                        ? "Free search used"
                        : `Charged ${formatLocalCurrencyOnly(analysis.chargedAmount, { showCode: true })}`}
                    </small>
                  </div>
                </div>

                <div className="market-analysis-summary-box">
                  <h3>Market Search Summary</h3>

                  <p>
                    <b>Market Country:</b>{" "}
                    {selectedCountryName || "selected country"}
                  </p>

                  <p>
                    <b>Search Keyword:</b> {analysis.searchKeyword}
                  </p>

                  {analysis.wallet && (
                    <p>
                      <b>Available Balance:</b>{" "}
                      {formatLocalCurrencyOnly(analysis.wallet.balanceAmount, {
                        showCode: true,
                      })}
                    </p>
                  )}
                </div>

                <div className="market-trend-section">
                  <div className="market-trend-header">
                    <div>
                      <h3>Price Fluctuation</h3>
                      <p>
                        Based on stored market search snapshots in the LyraZone
                        Database for this keyword and location.
                      </p>
                    </div>

                    <div className="market-trend-range-tabs">
                      <button
                        type="button"
                        className={trendRange === 30 ? "active" : ""}
                        onClick={() => handleTrendRangeChange(30)}
                      >
                        Last 30 Days
                      </button>

                      <button
                        type="button"
                        className={trendRange === 90 ? "active" : ""}
                        onClick={() => handleTrendRangeChange(90)}
                      >
                        3 Months
                      </button>
                    </div>
                  </div>

                  {trendLoading ? (
                    <div className="market-trend-empty">
                      Loading price fluctuation...
                    </div>
                  ) : (
                    <MarketPriceTrendChart
                      points={marketTrendPoints}
                      formatMarketResultCurrency={(value) =>
                        formatMarketResultCurrency(value, marketCurrencyCode)
                      }
                    />
                  )}

                  {aiSuggestion && (
                    <div className="market-ai-suggestion-grid">
                      <div className="market-ai-suggestion-card seller">
                        <span>AI Seller Suggestion</span>

                        <h4>
                          Suggested Price:{" "}
                          {aiSuggestion.seller.suggestedPrice
                            ? formatMarketResultCurrency(
                                aiSuggestion.seller.suggestedPrice,
                                marketCurrencyCode,
                              )
                            : "-"}
                        </h4>

                        <p>{aiSuggestion.seller.message}</p>
                      </div>

                      <div className="market-ai-suggestion-card buyer">
                        <span>AI Buyer Suggestion</span>

                        <h4>
                          Target Buy Price:{" "}
                          {aiSuggestion.buyer.targetPrice
                            ? formatMarketResultCurrency(
                                aiSuggestion.buyer.targetPrice,
                                marketCurrencyCode,
                              )
                            : "-"}
                        </h4>

                        <p>{aiSuggestion.buyer.message}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="market-analysis-results-section">
                  <div className="market-analysis-results-header">
                    <h3>Market Price Results</h3>
                    <span>{marketResultRows.length} Result(s)</span>
                  </div>

                  <div className="market-analysis-desktop-table">
                    <DataTable
                      columns={marketResultColumns}
                      data={marketResultRows}
                      getRowKey={(row, index) =>
                        row.id || `${row.title}-${row.sellerName}-${index}`
                      }
                    />
                  </div>

                  <div className="market-analysis-mobile-cards">
                    {marketResultRows.length === 0 ? (
                      <div className="market-analysis-mobile-empty">
                        No market price results found.
                      </div>
                    ) : (
                      marketResultRows.map((row, index) => (
                        <div
                          className="market-analysis-mobile-card"
                          key={
                            row.id || `${row.title}-${row.sellerName}-${index}`
                          }
                        >
                          <div className="market-analysis-mobile-status">
                            <span
                              className={`market-analysis-mobile-status-badge ${String(
                                row.matchType || "Unmatched",
                              ).toLowerCase()}`}
                            >
                              {row.matchType || "Unmatched"}
                            </span>
                          </div>

                          <div className="market-analysis-mobile-field">
                            <span>Market Product</span>
                            <strong>{row.title || "-"}</strong>
                          </div>

                          <div className="market-analysis-mobile-field">
                            <span>Seller</span>
                            <strong>{row.sellerName || "-"}</strong>
                          </div>

                          <div className="market-analysis-mobile-info-row">
                            <div className="market-analysis-mobile-info-item">
                              <span>Price</span>
                              <strong>
                                {formatMarketResultCurrency(
                                  row.priceValue,
                                  getMarketRowCurrencyCode(row),
                                )}
                              </strong>
                            </div>

                            <div className="market-analysis-mobile-info-item">
                              <span>Score</span>
                              <strong>{row.matchScore || 0}%</strong>
                            </div>

                            <div className="market-analysis-mobile-info-item">
                              <span>Rating</span>
                              <strong>
                                {row.rating
                                  ? `${row.rating} (${row.reviewsCount || 0})`
                                  : "-"}
                              </strong>
                            </div>
                          </div>

                          {row.url ? (
                            <a
                              href={row.url}
                              target="_blank"
                              rel="noreferrer"
                              className="market-analysis-mobile-view-btn"
                              onClick={(event) => event.stopPropagation()}
                            >
                              View Source
                            </a>
                          ) : (
                            <button
                              type="button"
                              className="market-analysis-mobile-view-btn disabled"
                              disabled
                            >
                              No Source
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  {analysis && (
                    <div className="market-analysis-result-actions bottom no-print">
                      <button
                        type="button"
                        className="market-analysis-export-btn"
                        onClick={handleExportSearchPdf}
                      >
                        Export Search
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </section>
        {walletModalOpen && (
          <div
            className="market-wallet-modal-overlay"
            onClick={() => setWalletModalOpen(false)}
          >
            <div
              className="market-wallet-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="market-wallet-modal-header">
                <div className="market-wallet-modal-balance">
                  <span>Market Analysis Wallet</span>
                  <h3>
                    {formatLocalCurrencyOnly(
                      walletHistory?.wallet?.balanceAmount ??
                        walletBalance ??
                        0,
                      { showCode: true },
                    )}
                  </h3>
                  <p>Available Balance</p>
                </div>

                <div className="market-wallet-modal-actions">
                  <button
                    type="button"
                    className="market-wallet-topup-btn"
                    onClick={handleWalletTopUp}
                  >
                    Top Up Balance
                  </button>

                  <button
                    type="button"
                    className="market-wallet-modal-close"
                    onClick={() => setWalletModalOpen(false)}
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="market-wallet-tabs">
                <button
                  type="button"
                  className={walletActiveTab === "transactions" ? "active" : ""}
                  onClick={() => setWalletActiveTab("transactions")}
                >
                  Transaction History
                </button>

                <button
                  type="button"
                  className={walletActiveTab === "topups" ? "active" : ""}
                  onClick={() => setWalletActiveTab("topups")}
                >
                  Top-up History
                </button>
              </div>

              <div className="market-wallet-modal-body">
                {walletHistoryLoading ? (
                  <div className="market-wallet-empty">
                    Loading wallet history...
                  </div>
                ) : walletHistoryError ? (
                  <div className="market-wallet-error">
                    {walletHistoryError}
                  </div>
                ) : walletActiveTab === "transactions" ? (
                  walletHistory?.transactions?.length ? (
                    <div className="market-wallet-list">
                      {walletHistory.transactions.map((item) => (
                        <div className="market-wallet-row" key={item.id}>
                          <div>
                            <strong>{item.transaction_type}</strong>

                            {item.search_keyword ? (
                              <>
                                <span>Search: {item.search_keyword}</span>
                                <small>
                                  {item.note || "-"} •{" "}
                                  {formatDateToDMY(item.created_at)}
                                </small>
                              </>
                            ) : (
                              <>
                                <span>
                                  {item.note || item.reference_type || "-"}
                                </span>
                                <small>
                                  {formatDateToDMY(item.created_at)}
                                </small>
                              </>
                            )}
                          </div>

                          <b
                            className={
                              item.transaction_type === "Debit"
                                ? "debit"
                                : item.transaction_type === "FreeSearch"
                                  ? "free"
                                  : "credit"
                            }
                          >
                            {item.transaction_type === "Debit" ? "-" : "+"}
                            {formatLocalCurrencyOnly(Number(item.amount || 0), {
                              showCode: true,
                            })}
                          </b>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="market-wallet-empty">
                      No transaction history found.
                    </div>
                  )
                ) : walletHistory?.topups?.length ? (
                  <div className="market-wallet-list">
                    {walletHistory.topups.map((item) => (
                      <div className="market-wallet-row" key={item.id}>
                        <div>
                          <strong>
                            {item.title || "Market Analysis Top-up"}
                          </strong>
                          <span>Ref: {item.payment_ref}</span>
                          <span>
                            Method: {getTopupPaymentMethodLabel(item)}
                          </span>
                          <small>{formatDateToDMY(item.created_at)}</small>
                        </div>

                        <div className="market-wallet-topup-right">
                          <b>
                            {formatLocalCurrencyOnly(Number(item.amount || 0), {
                              showCode: true,
                            })}
                          </b>

                          <span className="market-wallet-payment-method">
                            {getTopupPaymentMethodLabel(item)}
                          </span>

                          <em
                            className={String(item.status || "").toLowerCase()}
                          >
                            {item.status}
                          </em>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="market-wallet-empty">
                    No top-up history found.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {analysis && (
        <div className="market-analysis-pdf-render-zone">
          <div ref={pdfReportRef} className="market-analysis-pdf-report">
            <section className="market-analysis-pdf-hero">
              <span className="market-analysis-pdf-eyebrow">
                LyraZone Solution
              </span>

              <h1>Market Price Analysis</h1>

              <p>
                Search{" "}
                {analysis.locationName ||
                  selectedCountryName ||
                  "selected country"}{" "}
                market pricing by product keyword and compare current online
                price ranges before making your purchase or pricing decision.
              </p>
            </section>

            <div className="market-analysis-pdf-content">
              <div className="market-analysis-summary">
                <div className="market-analysis-card">
                  <span>Lowest Market Price</span>
                  <strong>
                    {formatMarketResultCurrency(
                      analysis.lowestPrice,
                      marketCurrencyCode,
                    )}
                  </strong>
                  <small>Based on market results</small>
                </div>

                <div className="market-analysis-card">
                  <span>Average Market Price</span>
                  <strong>
                    {formatMarketResultCurrency(
                      analysis.averagePrice,
                      marketCurrencyCode,
                    )}
                  </strong>
                  <small>
                    {analysis.locationName || selectedCountryName} market
                    average
                  </small>
                </div>

                <div className="market-analysis-card">
                  <span>Highest Market Price</span>
                  <strong>
                    {formatMarketResultCurrency(
                      analysis.highestPrice,
                      marketCurrencyCode,
                    )}
                  </strong>
                  <small>Based on market results</small>
                </div>

                <div className="market-analysis-card">
                  <span>Results Found</span>
                  <strong>{analysis.resultCount}</strong>
                  <small>
                    Charged{" "}
                    {formatLocalCurrencyOnly(analysis.chargedAmount || 0, {
                      showCode: true,
                    })}
                  </small>
                </div>
              </div>

              <div className="market-analysis-summary-box">
                <h3>Market Search Summary</h3>

                <p>
                  <b>Market Country:</b>{" "}
                  {analysis.locationName || selectedCountryName || "-"}
                </p>

                <p>
                  <b>Search Keyword:</b> {analysis.searchKeyword}
                </p>

                <p>
                  <b>Available Balance:</b>{" "}
                  {formatLocalCurrencyOnly(
                    analysis.wallet?.balanceAmount ?? walletBalance ?? 0,
                    { showCode: true },
                  )}
                </p>
              </div>

              <div className="market-trend-section">
                <div className="market-trend-header">
                  <div>
                    <h3>Price Fluctuation</h3>
                    <p>
                      Based on stored market search snapshots in the LyraZone
                      Database for this keyword and location.
                    </p>
                  </div>
                </div>

                <MarketPriceTrendChart
                  points={marketTrendPoints}
                  formatMarketResultCurrency={(value) =>
                    formatMarketResultCurrency(value, marketCurrencyCode)
                  }
                />
              </div>

              {aiSuggestion && (
                <div className="market-ai-suggestion-grid">
                  <div className="market-ai-suggestion-card seller">
                    <span>AI Seller Suggestion</span>

                    <h4>
                      Suggested Price:{" "}
                      {aiSuggestion.seller.suggestedPrice
                        ? formatMarketResultCurrency(
                            aiSuggestion.seller.suggestedPrice,
                            marketCurrencyCode,
                          )
                        : "-"}
                    </h4>

                    <p>{aiSuggestion.seller.message}</p>
                  </div>

                  <div className="market-ai-suggestion-card buyer">
                    <span>AI Buyer Suggestion</span>

                    <h4>
                      Target Buy Price:{" "}
                      {aiSuggestion.buyer.targetPrice
                        ? formatMarketResultCurrency(
                            aiSuggestion.buyer.targetPrice,
                            marketCurrencyCode,
                          )
                        : "-"}
                    </h4>

                    <p>{aiSuggestion.buyer.message}</p>
                  </div>
                </div>
              )}

              <div className="market-analysis-results-section">
                <div className="market-analysis-results-header">
                  <h3>Market Price Results</h3>
                  <span>{marketResultRows.length} Result(s)</span>
                </div>

                <DataTable
                  columns={marketResultColumns}
                  data={marketResultRows}
                  getRowKey={(row, index) =>
                    row.id || `${row.title}-${row.sellerName}-${index}`
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MarketPriceAnalysis;
